import { useEffect, useState } from 'react';
import { armGestureUnlock, setSoundEnabled, soundEnabled, soundSupported } from '../lib/sound.js';
import styles from './SoundButton.module.css';

export const OFF_PATH = 'M3 9v6h4l5 5V4L7 9H3zm13.5 3 3 3 1.5-1.5-3-3 3-3L19.5 6l-3 3-3-3L12 7.5l3 3-3 3L13.5 15l3-3z';
export const ON_PATH =
  'M3 9v6h4l5 5V4L7 9H3zm11.5 3c0-1.77-1-3.29-2.5-4.03v8.05c1.5-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z';

/**
 * The table's mute switch. It starts off, and the click that turns it on is
 * also the gesture a browser insists on before a page may make noise — so the
 * very first cue after the toggle actually sounds.
 *
 * `remote` is the host's choice from the phone's Host controls. The button
 * takes it up every time it changes, which is how a cast device with no
 * pointer gets muted or unmuted; a tap here still wins until the host flips
 * it again.
 */
export function SoundButton({ remote }: { remote?: boolean } = {}) {
  const [on, setOn] = useState(() => soundEnabled());

  // A reload keeps "on" but loses the unlocked context; the next tap restores it.
  useEffect(() => armGestureUnlock(), []);

  useEffect(() => {
    if (remote === undefined) return;
    setSoundEnabled(remote);
    setOn(remote);
  }, [remote]);

  if (!soundSupported()) return null;

  const label = on ? 'Sound on — mute the table' : 'Sound off — unmute the table';
  return (
    <button
      type="button"
      className={[styles.button, on ? styles.live : ''].filter(Boolean).join(' ')}
      aria-pressed={on}
      title={label}
      aria-label={label}
      onClick={() => {
        setSoundEnabled(!on);
        setOn(!on);
      }}
    >
      <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
        <path d={on ? ON_PATH : OFF_PATH} />
      </svg>
      <span>{on ? 'Sound' : 'Muted'}</span>
    </button>
  );
}
